import { clsx } from 'clsx'
import parse from 'html-react-parser'
import { type ComponentPropsWithRef, type ReactElement } from 'react'

import { IconSprites } from '@constants/enums/sprites'

import { Column } from '@components/ui-core/column/column'
import { Icon } from '@components/ui-core/icon/icon'
import { Row } from '@components/ui-core/row/row'

import { HeroCard } from '@custom/components/hero-card/hero-card'

import { HomepageText } from '../text/homepage-text'

import styles from './editor.module.css'

type EditorProps = ComponentPropsWithRef<'div'>

const TOOLBAR_CONTENT = [
  {
    id: 0,
    icon: IconSprites.Font,
    hasOptions: true,
  },
  {
    id: 1,
    icon: IconSprites.Bold,
    hasOptions: false,
  },
  {
    id: 2,
    icon: IconSprites.Italic,
    hasOptions: false,
  },
  {
    id: 3,
    icon: IconSprites.Underline,
    hasOptions: false,
  },
  {
    id: 4,
    icon: IconSprites.Strikethrough,
    hasOptions: false,
  },
  {
    id: 5,
    icon: IconSprites.AlignStart,
    hasOptions: true,
  },
]

const EDITOR_CONTENT = {
  title: 'Streamlining Team Documentation',
  text: "I've been thinking about how our team can streamline the onboarding process for <u>new-hires</u>. <span>Right now, documentation is scattered across different tools, which makes it hard to find answers quickly. If we consolidate into a single hub and add</span> more real-world examples, I think it'll cut down on <u>repetative</u> questions and help people ramp up faster.",
  toolbar: TOOLBAR_CONTENT,
}

export function Editor(props: EditorProps): ReactElement {
  const { className, ...rest } = props
  const { title, text, toolbar } = EDITOR_CONTENT

  return (
    <HeroCard className={clsx(styles.editor, className)} {...rest}>
      <Column gap="4x">
        <Row crossAxisAlignment="center" gap="2x">
          <Icon variant={IconSprites.Home} />
          <HomepageText as="span" variant="heroCard" className={styles.toolbarText}>
            Team workspace
          </HomepageText>

          <HomepageText
            as="span"
            variant="heroCard"
            className={clsx(styles.toolbarText, styles.toolbarTextEnd)}
          >
            Share
          </HomepageText>
          <Icon variant={IconSprites.Comment} />
          <Icon variant={IconSprites.Gear} />
        </Row>
        <Column gap="3x">
          <HomepageText as="span" variant="heroCard">
            {title}
          </HomepageText>
          <HomepageText as="span" variant="heroCard" className={styles.editorText}>
            {parse(text)}
          </HomepageText>
          <Row crossAxisAlignment="center" className={styles.toolbar}>
            {toolbar.map(({ id, icon, hasOptions }) => (
              <Row key={id} crossAxisAlignment="center" gap="0.5x" className={styles.toolbarItem}>
                <Icon variant={icon} />
                {hasOptions && (
                  <Icon variant={IconSprites.ArrowDown} className={styles.arrowDown} />
                )}
              </Row>
            ))}
          </Row>
        </Column>
      </Column>
    </HeroCard>
  )
}
